import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import { Link } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import { getSites, getUsers } from '../services/api';
import { MapPin } from 'lucide-react';

const DEFAULT_PALETTE = ['#0f766e', '#7c3aed', '#0369a1', '#b45309', '#be185d',
                        '#059669', '#2563eb', '#9333ea', '#dc2626', '#ea580c'];

// UK-centred fallback view — used when no site has lat/lng yet.
const UK_CENTRE = [54.5, -2.5];

/**
 * SiteMap — every customer site with lat/lng plotted as a coloured
 * dot on OpenStreetMap. Pin colour follows the customer's chartColor
 * (same one used on the Monthly Margin chart) so the map + chart
 * legend visually match. Hover shows customer + site name, click
 * opens the site.
 */
const SiteMap = ({ height = 340 }) => {
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getSites().catch(() => []), getUsers().catch(() => [])])
      .then(([s, u]) => {
        setSites((s || []).filter((x) => x.lat != null && x.lng != null));
        setCustomers((u || []).filter((x) => x.role === 'customer'));
      })
      .catch(() => toast.error('Failed to load map data'))
      .finally(() => setLoading(false));
  }, []);

  // Map customerId → colour. Prefer chartColor, else assign from palette
  // by index of customer (stable order = alphabetical by name).
  const colourFor = useMemo(() => {
    const sorted = [...customers].sort((a, b) => (a.company || a.name || '').localeCompare(b.company || b.name || ''));
    const m = new Map();
    sorted.forEach((c, i) => {
      m.set(c.id, c.chartColor || DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]);
    });
    return m;
  }, [customers]);

  // Auto-fit bounds from the sites — default UK view if empty.
  const initialCentre = useMemo(() => {
    if (sites.length === 0) return UK_CENTRE;
    const lat = sites.reduce((s, x) => s + x.lat, 0) / sites.length;
    const lng = sites.reduce((s, x) => s + x.lng, 0) / sites.length;
    return [lat, lng];
  }, [sites]);

  if (loading) {
    return (
      <div
        className="bg-white border border-slate-200 rounded-md flex items-center justify-center text-sm text-slate-500"
        style={{ height }}
      >
        Loading map…
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div
        className="bg-white border border-slate-200 rounded-md flex flex-col items-center justify-center gap-2 text-sm text-slate-500 p-6 text-center"
        style={{ height }}
        data-testid="site-map-empty"
      >
        <MapPin className="h-6 w-6 text-slate-300" />
        <div>No site coordinates yet.</div>
        <div className="text-xs text-slate-400">
          Add a postcode to a customer site and lat/lng will fill automatically — new sites will start appearing on the map.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="site-map">
      <MapContainer
        center={initialCentre}
        zoom={sites.length === 1 ? 12 : 6}
        style={{ height, width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {sites.map((s) => {
          const colour = colourFor.get(s.customerId) || '#64748b';
          return (
            <CircleMarker
              key={s.id}
              center={[s.lat, s.lng]}
              radius={7}
              pathOptions={{
                color: '#ffffff',
                weight: 2,
                fillColor: colour,
                fillOpacity: 0.9,
              }}
              data-testid={`site-map-pin-${s.id}`}
            >
              <Tooltip direction="top" offset={[0, -6]}>
                <div className="text-xs">
                  <div className="font-semibold text-slate-900">{s.name}</div>
                  <div className="text-slate-500">{s.customerName || 'Unknown customer'}</div>
                </div>
              </Tooltip>
              <Popup>
                <div className="text-xs space-y-1">
                  <div className="font-semibold text-slate-900">{s.name}</div>
                  {s.address && <div className="text-slate-600">{s.address}</div>}
                  <div className="text-slate-500">{s.customerName || 'Unknown customer'}</div>
                  {s.customerId && (
                    <Link
                      to={`/admin/customers/${s.customerId}/sites/${s.id}`}
                      className="text-blue-700 hover:text-blue-800 inline-block mt-1"
                    >
                      Open site →
                    </Link>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default SiteMap;
